import { AppDispatch, AppStorageType } from '@app-types/appStorageType';
import { apiCall } from '@commands/httpClient';
import { IInitiatedStatisticResp, InitiatedStatistic } from '@modules/schedule';
import { IScheduleSlot, ScheduleSlot } from '@modules/schedule/schedule-type';
import {
  detachSlot,
  replaceTeacherSlots,
  setLessonStatistic,
} from '@modules/schedule/actions';
import { ResultListRes } from '@modules/apiClient/type';
import { BaseCommandProps } from '@app-types/CommonTypes';
import { selectStudent, selectTeacher, clearAdminSchedule } from '@modules/admin/actions';
import { loadTeacherTimetable } from '@commands/schedule/index';
import { loadPurchaseFromUser } from '@commands/purchase/purchase';

export const loadScheduleStatistic = (userId: number) => (dispatch: AppDispatch<IScheduleSlot[]>) => {
  return dispatch(apiCall<object, IInitiatedStatisticResp>(`/v1/admin/schedule/initiated/stats`, { userId }, {
    method: 'post',
    silent: true,
  }))
    .then((resp) => {
      const stat = resp && new InitiatedStatistic(resp);
      resp && dispatch(setLessonStatistic(stat));
      return stat;
    });
};

export const assignTeacher = () => (dispatch: AppDispatch<ResultListRes<IScheduleSlot[]>>, getState: () => AppStorageType) => {
  const state: AppStorageType = getState();
  const teacherId = state.adminSchedule.teacher;
  const userId = state.adminSchedule.student;

  if (!userId || !teacherId) {
    return;
  }

  dispatch(apiCall<object, ResultListRes<void>>(`/v1/admin/schedule/initiated/attach`, { id: teacherId, userId }, {
    method: 'post',
    context: 'main',
  }))
    .then(() => {
      dispatch(loadScheduleStatistic(userId));
    });
};

export const attachLesson = (id: number) => (dispatch: AppDispatch<ResultListRes<IScheduleSlot[]>>, getState: () => AppStorageType) => {
  const state: AppStorageType = getState();
  const userId = state.adminSchedule.student;

  if (!userId) {
    return;
  }

  dispatch(apiCall<object, ResultListRes<IScheduleSlot[]>>(`/v1/admin/schedule/initiated/attach-slot`, {
    id,
    userId,
  }, { method: 'post' }))
    .then(({ list }) => {
      const items = (list || []).map(i => new ScheduleSlot(i));
      dispatch(replaceTeacherSlots(items));
      dispatch(loadScheduleStatistic(userId));
    });
};

export const createWorkslot = (date: number, count: number) => (dispatch: AppDispatch<ResultListRes<IScheduleSlot[]>>, getState: () => AppStorageType) => {
  const state: AppStorageType = getState();
  const userId = state.adminSchedule.teacher;

  if (!userId) {
    return;
  }

  dispatch(apiCall<object, ResultListRes<IScheduleSlot[]>>(`/v1/admin/schedule/initiator`, {
    date,
    duration: count,
    userId,
  }, { method: 'post' }))
    .then(({ list }) => {
      const items = (list || []).map(i => new ScheduleSlot(i));
      dispatch(replaceTeacherSlots(items));
    });
};

export const cancelLessonByInitiated = (id: number, offset: number, userOffset?: number) => (dispatch: AppDispatch<ResultListRes<IScheduleSlot>>, getState: () => AppStorageType) => {
  const state: AppStorageType = getState();
  const userId = state.adminSchedule.student;

  if (!userId) {
    return;
  }

  dispatch(apiCall<object, BaseCommandProps>(`/v1/admin/schedule/initiated/cancel-lesson`, { id, userId }, {
    method: 'post',
    context: 'main',
  }))
    .then(() => {
      const teacherId = state.adminSchedule.teacher;
      if (teacherId) {
        dispatch(loadTeacherTimetable(teacherId, offset, userOffset));
      }
      dispatch(loadScheduleStatistic(userId));
    });
};

export const cancelLessonByInitiator = (id: number) => (dispatch: AppDispatch<ResultListRes<IScheduleSlot>>, getState: () => AppStorageType) => {
  const state: AppStorageType = getState();
  const userId = state.adminSchedule.teacher;

  if (!userId) {
    return;
  }

  dispatch(apiCall<object, BaseCommandProps>(`/v1/admin/schedule/initiator/cancel-lesson`, { id, userId }, {
    method: 'post',
    context: 'main',
  }))
    .then(() => {
      dispatch(detachSlot(id));
      dispatch(loadScheduleStatistic(userId));
    });
};

export const selectStudentByAdmin = (id: number, loadTeacher: boolean=false) => (dispatch: AppDispatch<ResultListRes<IScheduleSlot>>) => {
  if (loadTeacher) {
    dispatch(clearAdminSchedule());
  }

  dispatch(selectStudent(id));
  const res = dispatch(loadScheduleStatistic(id));

  if (loadTeacher) {
    res.then((stat) => dispatch(selectTeacher(stat?.assignedTeacherId)));
  }
};

export const changeTeacherForCourse = (teacherId: number, userId: number, purchaseId: number) => (dispatch: AppDispatch<ResultListRes<IScheduleSlot>>, getState: () => AppStorageType) => {
  return dispatch(apiCall<object, BaseCommandProps>(`/v1/purchase/admin/change-teacher`, {
    userId,
    teacherId,
    purchaseId,
  }, {
    method: 'post',
  }));
};

export const deleteCourse = (userId: number, purchaseId: number) => (dispatch: AppDispatch<ResultListRes<void>>, getState: () => AppStorageType) => {
  return dispatch(apiCall<object, BaseCommandProps>(`/v1/purchase/admin/withdrawal`, {
    userId,
    purchaseId,
  }, {
    method: 'post',
  }));
};

export const deleteTeacherForCourse = (userId: number, purchaseId: number) => (dispatch: AppDispatch<ResultListRes<IScheduleSlot>>, getState: () => AppStorageType) => {
  return dispatch(apiCall<object, BaseCommandProps>(`/v1/purchase/admin/delete-teacher`, {
    userId,
    purchaseId,
  }, {
    method: 'post',
  })).then(() => dispatch(loadPurchaseFromUser(userId)));
};

